/**
 * CompetitiveEdgeModal — the "design something better than X and Y" experience.
 *
 * Flow:
 *   1. open with prompt + language
 *   2. POST /api/project/competitive-edge → research loading state
 *   3. show competitor analysis cards + innovation gaps + must-beat metrics
 *   4. user confirms → callback fires with the enriched intent ready for generate-full
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, X, Check, AlertCircle, Loader2, Zap, TrendingUp,
  Cpu, Sparkles, Target, Award, Globe, ExternalLink,
} from 'lucide-react';
import { API_URL } from '@/utils/apiUrl';


export default function CompetitiveEdgeModal({
  open, prompt, language = 'tr', projectType = 'product',
  onClose, onConfirm,
}) {
  const [stage, setStage] = useState('idle'); // idle | researching | review | error
  const [edge, setEdge] = useState(null);
  const [fallback, setFallback] = useState(false);
  const [error, setError] = useState(null);
  const [researchSources, setResearchSources] = useState([]);
  const [researchPerformed, setResearchPerformed] = useState(false);

  const T = useMemo(() => language === 'tr' ? {
    title: 'Rekabetçi Tasarım Motoru',
    sub: 'Web canlı araştırması ile rakipleri analiz ediyoruz, inovasyon boşluklarını buluyoruz, tüm metrikleri yenecek bir tasarım üretiyoruz',
    researching: 'Rakipler araştırılıyor… inovasyon boşlukları belirleniyor…',
    competitors: 'Rakipler',
    gaps: 'İnovasyon Boşlukları',
    gapSub: 'Hiçbir rakibin sunmadığı yenilikler',
    mustBeat: 'Yenilmesi Gereken Metrikler',
    youGet: 'Senin Tasarımın Bu Özellikleri Getirecek',
    summary: 'Farklılaşma Özeti',
    cancel: 'Vazgeç',
    generate: 'Daha İyisini Üret',
    errTitle: 'Bütçe veya servis sorunu',
    competitorBest: 'Rakip en iyisi',
    target: 'Hedef',
    fallbackHint: 'AI servisi şu an dolu. Manuel olarak devam edebilir veya tekrar deneyebilirsiniz.',
    flagship: 'Bayrak gemisi',
    strengths: 'Güçlü yönler',
    weaknesses: 'Zayıf yönler',
    propTech: 'Patentli/özel teknoloji',
    sourcesTitle: 'Canlı Web Araştırması',
    sourcesSub: 'Bu analiz, az önce internetten çekilen güncel kaynaklardan hazırlandı',
    liveResearch: 'CANLI',
    visit: 'Kaynağı aç',
  } : {
    title: 'Competitive Design Engine',
    sub: 'Live web research analyzes competitors, finds innovation gaps, and synthesizes a design that beats them on every metric',
    researching: 'Researching competitors live on the web… identifying innovation gaps…',
    competitors: 'Competitors',
    gaps: 'Innovation Gaps',
    gapSub: 'Technologies no competitor offers today',
    mustBeat: 'Metrics to Beat',
    youGet: 'Your Design Will Include',
    summary: 'Differentiation Summary',
    cancel: 'Cancel',
    generate: 'Generate the better design',
    errTitle: 'Budget or service issue',
    competitorBest: 'Competitor best',
    target: 'Target',
    fallbackHint: 'AI service is currently unavailable. You can proceed manually or try again later.',
    flagship: 'Flagship',
    strengths: 'Strengths',
    weaknesses: 'Weaknesses',
    propTech: 'Proprietary tech',
    sourcesTitle: 'Live Web Research',
    sourcesSub: 'This analysis was built from current sources fetched seconds ago',
    liveResearch: 'LIVE',
    visit: 'Open source',
  }, [language]);

  useEffect(() => {
    if (!open) {
      setStage('idle'); setEdge(null); setError(null); setFallback(false);
      setResearchSources([]); setResearchPerformed(false);
      return;
    }
    if (open && stage === 'idle') {
      runResearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const runResearch = async () => {
    if (!prompt?.trim()) return;
    setStage('researching');
    setError(null); setEdge(null); setFallback(false);
    try {
      const storedToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/project/competitive-edge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ prompt, language, project_type: projectType }),
      });
      const data = await res.json();
      if (data && data.edge) {
        setEdge(data.edge);
        setFallback(!!data.fallback);
        setResearchSources(data.research_sources || []);
        setResearchPerformed(!!data.research_performed);
        if (data.fallback) setError(data.error || T.errTitle);
        setStage('review');
      } else {
        setError(data?.detail || 'Edge engine failed');
        setStage('error');
      }
    } catch (err) {
      setError(err.message || String(err));
      setStage('error');
    }
  };

  const handleConfirm = () => {
    if (!edge?.enriched_intent) return;
    // Attach research provenance to the edge payload so the project can persist it
    // and the public project page can show the live source URLs.
    const enrichedEdge = {
      ...edge,
      research_sources: researchSources,
      research_performed: researchPerformed,
    };
    onConfirm(edge.enriched_intent, enrichedEdge);
  };

  if (!open) return null;

  const competitors = edge?.competitor_analysis || [];
  const gaps = edge?.innovation_gaps || [];
  const metrics = edge?.must_beat_metrics || [];
  const features = edge?.innovation_features || [];

  return (
    <AnimatePresence>
      <motion.div
        data-testid="edge-modal"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
      >
        <motion.div
          initial={{ y: 28, scale: 0.97, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 16, opacity: 0 }}
          transition={{ type: 'spring', damping: 22, stiffness: 220 }}
          className="w-full max-w-4xl max-h-[92vh] overflow-hidden rounded-2xl border border-amber-500/30 bg-[#060B1A] shadow-[0_0_80px_-20px_rgba(245,158,11,0.55)] flex flex-col"
        >
          {/* Header */}
          <div className="relative border-b border-white/10 bg-gradient-to-br from-amber-500/10 via-transparent to-cyan-500/5 px-6 py-5 flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-2">
                  <Trophy className="h-5 w-5 text-amber-300" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white" data-testid="edge-modal-title">{T.title}</h2>
                  <p className="mt-1 text-xs text-white/60 max-w-2xl">{T.sub}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1 text-white/40 transition hover:bg-white/5 hover:text-white"
                data-testid="edge-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body — scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {/* RESEARCHING */}
            {stage === 'researching' && (
              <div className="flex flex-col items-center py-16 text-center" data-testid="edge-researching">
                <Loader2 className="h-10 w-10 animate-spin text-amber-400" />
                <p className="mt-5 text-sm text-white/70 max-w-md">{T.researching}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                  <span className="relative inline-flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  </span>
                  <Globe className="h-3 w-3" />
                  {T.liveResearch}
                </div>
                <div className="mt-6 flex flex-col gap-1.5 text-[11px] text-white/40 max-w-sm">
                  <ResearchStep label="Detecting competitor brands" delay={0} />
                  <ResearchStep label="Live web search via Tavily AI" delay={1.2} />
                  <ResearchStep label="Analyzing latest specs and patents" delay={2.4} />
                  <ResearchStep label="Identifying innovation gaps" delay={3.6} />
                  <ResearchStep label="Synthesizing locked design brief" delay={4.8} />
                </div>
              </div>
            )}

            {/* REVIEW */}
            {stage === 'review' && edge && (
              <div className="space-y-6">
                {fallback && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{T.fallbackHint}</span>
                  </div>
                )}

                {/* Live Web Research provenance panel */}
                {researchPerformed && researchSources.length > 0 && (
                  <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 p-4" data-testid="edge-sources-panel">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-emerald-300">
                        <Globe className="h-3.5 w-3.5" />
                        {T.sourcesTitle}
                      </div>
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        {T.liveResearch}
                      </span>
                    </div>
                    <p className="mb-3 text-[11px] text-white/50">{T.sourcesSub}</p>
                    <ul className="space-y-1.5">
                      {researchSources.slice(0, 8).map((s, i) => (
                        <li key={i} data-testid={`edge-source-${i}`} className="flex items-start gap-2">
                          <span className="mt-1 inline-flex items-center justify-center min-w-[42px] rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold text-emerald-300">
                            {s.brand?.toUpperCase()}
                          </span>
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex-1 text-xs text-white/80 hover:text-white"
                          >
                            <span className="line-clamp-1 underline decoration-emerald-500/30 underline-offset-2 group-hover:decoration-emerald-400">
                              {s.title || s.url}
                            </span>
                            <span className="mt-0.5 line-clamp-1 text-[10px] text-white/30">
                              {(() => { try { return new URL(s.url).hostname.replace('www.', ''); } catch (e) { return s.url; } })()}
                              <ExternalLink className="inline h-2.5 w-2.5 ml-1 opacity-50" />
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Differentiation summary */}
                {edge.differentiation_summary && (
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                    <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-emerald-300">
                      <Sparkles className="h-3.5 w-3.5" /> {T.summary}
                    </div>
                    <p className="text-base font-medium text-white leading-snug" data-testid="edge-summary">
                      "{edge.differentiation_summary}"
                    </p>
                  </div>
                )}

                {/* Competitor cards */}
                {competitors.length > 0 && (
                  <Section label={T.competitors} icon={<TrendingUp className="h-3.5 w-3.5" />}>
                    <div className="grid gap-3 md:grid-cols-2">
                      {competitors.map((c, i) => (
                        <div key={i} className="rounded-lg border border-white/10 bg-black/30 p-3.5 overflow-hidden" data-testid={`edge-competitor-${i}`}>
                          <div className="mb-2 flex items-baseline justify-between gap-2">
                            <span className="text-base font-bold text-white">{c.brand}</span>
                            {c.flagship_in_category && (
                              <span className="text-[10px] text-white/40 truncate">{c.flagship_in_category}</span>
                            )}
                          </div>
                          {/* Key specs */}
                          {c.key_specs && Object.keys(c.key_specs).length > 0 && (
                            <div className="mb-2 grid gap-1 text-[11px]">
                              {Object.entries(c.key_specs).slice(0, 5).map(([k, v]) => (
                                <div key={k} className="grid grid-cols-[40%_60%] gap-2 items-baseline">
                                  <span className="text-white/50 truncate">{k}</span>
                                  <span className="text-white/90 font-mono text-right truncate" title={String(v)}>{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {c.strengths?.length > 0 && (
                            <Bullets color="emerald" items={c.strengths.slice(0, 3)} label={T.strengths} />
                          )}
                          {c.weaknesses?.length > 0 && (
                            <Bullets color="rose"     items={c.weaknesses.slice(0, 3)}   label={T.weaknesses} />
                          )}
                          {c.patented_or_proprietary_tech?.length > 0 && (
                            <Bullets color="cyan"    items={c.patented_or_proprietary_tech.slice(0, 3)} label={T.propTech} />
                          )}
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Innovation gaps */}
                {gaps.length > 0 && (
                  <Section label={T.gaps} icon={<Target className="h-3.5 w-3.5" />}>
                    <p className="mb-3 text-[11px] text-white/40">{T.gapSub}</p>
                    <ul className="space-y-2">
                      {gaps.map((g, i) => (
                        <li key={i} data-testid={`edge-gap-${i}`} className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 text-sm text-white/85">
                          <Zap className="h-4 w-4 mt-0.5 text-amber-300 flex-shrink-0" />
                          <span>{g}</span>
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                {/* Must-beat metrics */}
                {metrics.length > 0 && (
                  <Section label={T.mustBeat} icon={<Award className="h-3.5 w-3.5" />}>
                    <div className="overflow-hidden rounded-lg border border-white/10">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-white/5 text-[10px] uppercase tracking-widest text-white/50">
                            <th className="px-3 py-2 text-left">Metric</th>
                            <th className="px-3 py-2 text-right">{T.competitorBest}</th>
                            <th className="px-3 py-2 text-right">{T.target}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {metrics.map((m, i) => (
                            <tr key={i} data-testid={`edge-metric-${i}`} className="border-t border-white/5">
                              <td className="px-3 py-2 text-white/85">{m.metric}</td>
                              <td className="px-3 py-2 text-right font-mono text-white/60">{m.competitor_best}</td>
                              <td className="px-3 py-2 text-right font-mono font-bold text-emerald-300">{m.target}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>
                )}

                {/* Innovation features the design will include */}
                {features.length > 0 && (
                  <Section label={T.youGet} icon={<Cpu className="h-3.5 w-3.5" />}>
                    <div className="grid gap-2 md:grid-cols-2">
                      {features.map((f, i) => (
                        <div key={i} data-testid={`edge-feature-${i}`} className="flex items-start gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5 text-sm text-white/90">
                          <Check className="h-4 w-4 mt-0.5 text-emerald-400 flex-shrink-0" />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}
              </div>
            )}

            {/* ERROR */}
            {stage === 'error' && (
              <div className="flex flex-col items-center py-10 text-center" data-testid="edge-error">
                <div className="rounded-full border border-rose-500/30 bg-rose-500/10 p-3">
                  <AlertCircle className="h-7 w-7 text-rose-400" />
                </div>
                <p className="mt-4 text-sm font-medium text-rose-200">{T.errTitle}</p>
                {error && (
                  <p className="mt-2 max-w-md text-xs text-white/40 leading-relaxed">{error}</p>
                )}
                <button
                  onClick={runResearch}
                  className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm font-medium text-amber-200 hover:bg-amber-500/20"
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          {stage === 'review' && edge?.enriched_intent && (
            <div className="flex items-center justify-between gap-3 border-t border-white/10 bg-black/30 px-6 py-4 flex-shrink-0">
              <button
                onClick={onClose}
                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/70 hover:border-white/30 hover:text-white"
                data-testid="edge-cancel"
              >
                {T.cancel}
              </button>
              <button
                onClick={handleConfirm}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-emerald-500 px-6 py-2.5 text-sm font-bold text-[#060B1A] shadow-lg shadow-amber-500/30 transition hover:shadow-amber-500/50"
                data-testid="edge-confirm"
              >
                <Trophy className="h-4 w-4" />
                {T.generate}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const Section = ({ label, icon, children }) => (
  <div>
    <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-cyan-300">
      {icon}
      <span>{label}</span>
    </div>
    {children}
  </div>
);

const Bullets = ({ color, items, label }) => {
  const palette = {
    emerald: 'text-emerald-300', rose: 'text-rose-300', cyan: 'text-cyan-300',
  }[color] || 'text-white/70';
  const dot = {
    emerald: 'bg-emerald-400', rose: 'bg-rose-400', cyan: 'bg-cyan-400',
  }[color] || 'bg-white/40';
  return (
    <div className="mt-2">
      <div className={`text-[9px] uppercase tracking-wider ${palette} mb-1`}>{label}</div>
      <ul className="space-y-0.5">
        {items.map((it, idx) => (
          <li key={idx} className="flex items-start gap-1.5 text-[11px] text-white/70 leading-snug">
            <span className={`mt-1.5 inline-block h-1 w-1 flex-shrink-0 rounded-full ${dot}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ResearchStep = ({ label, delay }) => (
  <motion.div
    initial={{ opacity: 0, x: -8 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: delay * 0.4 }}
    className="flex items-center gap-2"
  >
    <span className="inline-block h-1 w-1 rounded-full bg-amber-300/60" />
    <span>{label}</span>
  </motion.div>
);
